import { useMachine } from '@xstate/react';
import { useCallback, useMemo, useEffect } from 'react';
import { createEntityMachine } from '@/machines/entityMachine';
import { getEntityConfig, EntityConfig } from '@/config/entities';
import { useToast } from '@/hooks/use-toast';

export interface UseEntityMachineOptions {
  autoFetch?: boolean;
  onCreateSuccess?: (item: any) => void;
  onUpdateSuccess?: (item: any) => void;
  onDeleteSuccess?: () => void;
}

export function useEntityMachine<T extends Record<string, any>>(
  entityName: string,
  options: UseEntityMachineOptions = {}
) {
  const { autoFetch = true, onCreateSuccess, onUpdateSuccess, onDeleteSuccess } = options;
  const config = useMemo(() => getEntityConfig<T>(entityName), [entityName]);
  const machine = useMemo(() => createEntityMachine<T>(config), [config]);
  const [snapshot, send] = useMachine(machine);
  const { toast } = useToast();

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      send({ type: 'FETCH' });
    }
  }, [autoFetch, send]);

  // Show error toast when error occurs
  useEffect(() => {
    if (snapshot.context.error) {
      toast({
        title: 'Error',
        description: snapshot.context.error,
        variant: 'destructive',
      });
    }
  }, [snapshot.context.error, toast]);

  const fetch = useCallback(() => {
    send({ type: 'FETCH' });
  }, [send]);

  const refresh = useCallback(() => {
    send({ type: 'REFRESH' });
  }, [send]);

  const create = useCallback(async (data: Partial<T>) => {
    send({ type: 'CREATE', data });
    toast({
      title: 'Success',
      description: `${config.singularName} created successfully`,
    });
    onCreateSuccess?.(data);
  }, [send, config.singularName, toast, onCreateSuccess]);

  const update = useCallback(async (id: string, data: Partial<T>) => {
    send({ type: 'UPDATE', id, data });
    toast({
      title: 'Success',
      description: `${config.singularName} updated successfully`,
    });
    onUpdateSuccess?.(data);
  }, [send, config.singularName, toast, onUpdateSuccess]);

  const remove = useCallback(async (id: string) => {
    send({ type: 'DELETE', id });
    toast({
      title: 'Success',
      description: `${config.singularName} deleted successfully`,
    });
    onDeleteSuccess?.();
  }, [send, config.singularName, toast, onDeleteSuccess]);

  const bulkDelete = useCallback(async (ids: string[]) => {
    send({ type: 'BULK_DELETE', ids });
    toast({
      title: 'Success',
      description: `${ids.length} ${ids.length === 1 ? config.singularName : config.pluralName} deleted successfully`,
    });
    onDeleteSuccess?.();
  }, [send, config.singularName, config.pluralName, toast, onDeleteSuccess]);

  const select = useCallback((item: T | null) => {
    send({ type: 'SELECT', item });
  }, [send]);

  const setFilters = useCallback((filters: Record<string, any>) => {
    send({ type: 'SET_FILTERS', filters });
  }, [send]);

  const setAdvancedFilters = useCallback((advancedFilters: any[]) => {
    send({ type: 'SET_ADVANCED_FILTERS', advancedFilters });
  }, [send]);

  const setSearch = useCallback((query: string) => {
    send({ type: 'SET_SEARCH', query });
  }, [send]);

  const setPage = useCallback((page: number) => {
    send({ type: 'SET_PAGE', page });
  }, [send]);

  const setPageSize = useCallback((pageSize: number) => {
    send({ type: 'SET_PAGE_SIZE', pageSize });
  }, [send]);

  const retry = useCallback(() => {
    send({ type: 'RETRY' });
  }, [send]);

  const dismissError = useCallback(() => {
    send({ type: 'DISMISS_ERROR' });
  }, [send]);

  return {
    // State
    items: snapshot.context.items,
    selectedItem: snapshot.context.selectedItem,
    loading: snapshot.context.loading,
    error: snapshot.context.error,
    pagination: snapshot.context.pagination,
    filters: snapshot.context.filters,
    advancedFilters: snapshot.context.advancedFilters,
    searchQuery: snapshot.context.searchQuery,
    config,

    // State helpers
    isIdle: snapshot.matches('idle'),
    isFetching: snapshot.matches('fetching'),
    isCreating: snapshot.matches('creating'),
    isUpdating: snapshot.matches('updating'),
    isDeleting: snapshot.matches('deleting'),
    isBulkDeleting: snapshot.matches('bulkDeleting'),
    hasError: snapshot.matches('error'),

    // Actions
    fetch,
    refresh,
    create,
    update,
    remove,
    bulkDelete,
    select,
    setFilters,
    setAdvancedFilters,
    setSearch,
    setPage,
    setPageSize,
    retry,
    dismissError,
  };
}
